import { useState } from "react";
import { useNavigate } from "react-router-dom";
import InputField from "../inputField/InputField";
import ButtonGradient from "../ui/ButtonGradient";
import SuccessToast from "../successToast/SuccessToast";
import EmailInputWithSuggestions from "../emailInputWithSuggestions/EmailInputWithSuggestions";
import { InputGroup } from "./RegisterFormStyles";
import { AnimatePresence } from "framer-motion";
import { Form, FormTitle, Tabs, Tab } from "../ui/FormBaseStyles";

import User from "../../img/form/user.png";
import Mail from "../../img/form/mail.png";
import Call from "../../img/form/call.png";
import Lock from "../../img/form/lock.png";

const RegisterForm = () => {
  const navigate = useNavigate();
  const [showSuccess, setShowSuccess] = useState(false);
  const [formValidated, setFormValidated] = useState(false);
  const [submitCount, setSubmitCount] = useState(0);

  const [formData, setFormData] = useState({
    name: "",
    lastName: "",
    artistName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({
    name: false,
    lastName: false,
    artistName: false,
    email: false,
    password: false,
    confirmPassword: false,
    passwordMismatch: false,
  });

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidPassword = (password: string): boolean => {
    const hasMinLength = password.length >= 6;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasSpecialChar = /[!@#$%^&*()\-_=+[\]{};:'",.<>/?\\|`~]/.test(
      password
    );
    return hasMinLength && hasUpperCase && hasSpecialChar;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitCount((prev) => prev + 1);

    const isEmpty = (value: string) => value.trim() === "";

    const newErrors = {
      name: isEmpty(formData.name),
      lastName: isEmpty(formData.lastName),
      artistName: isEmpty(formData.artistName),
      email: isEmpty(formData.email) || !isValidEmail(formData.email),
      password:
        isEmpty(formData.password) || !isValidPassword(formData.password),
      confirmPassword: isEmpty(formData.confirmPassword),
      passwordMismatch: formData.password !== formData.confirmPassword,
    };

    setErrors(newErrors);
    setFormValidated(true);

    const hasError = Object.values(newErrors).some(Boolean);
    if (hasError) return;

    setShowSuccess(true);

    setTimeout(() => {
      setShowSuccess(false);
      navigate("/login");
    }, 2000);

    setFormData({
      name: "",
      lastName: "",
      artistName: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
    });

    setErrors({
      name: false,
      lastName: false,
      artistName: false,
      email: false,
      password: false,
      confirmPassword: false,
      passwordMismatch: false,
    });

    setFormValidated(false);
  };

  return (
    <Form onSubmit={handleSubmit} autoComplete="off">
      <Tabs>
        <Tab $active>Cadastrar</Tab>
        <Tab onClick={() => navigate("/login")}>Logar</Tab>
      </Tabs>

      <FormTitle>Crie sua conta</FormTitle>

      <InputGroup>
        <InputField
          icon={<img src={User} alt="ícone usuário" />}
          label="Nome"
          name="name"
          placeholder="Escreva seu nome"
          required
          autoComplete="off"
          value={formData.name}
          error={formValidated && errors.name ? "Preencha o nome" : ""}
          shakeTrigger={formValidated && errors.name ? submitCount : 0}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />

        <InputField
          icon={<img src={User} alt="ícone usuário" />}
          label="Sobrenome"
          name="lastName"
          placeholder="Escreva seu sobrenome"
          required
          autoComplete="off"
          value={formData.lastName}
          error={formValidated && errors.lastName ? "Preencha o sobrenome" : ""}
          shakeTrigger={formValidated && errors.lastName ? submitCount : 0}
          onChange={(e) =>
            setFormData({ ...formData, lastName: e.target.value })
          }
        />

        <InputField
          icon={<img src={User} alt="ícone usuário" />}
          label="Nome Artístico"
          name="artistName"
          placeholder="Escreva seu nome artístico"
          required
          autoComplete="off"
          value={formData.artistName}
          error={
            formValidated && errors.artistName
              ? "Preencha o nome artístico"
              : ""
          }
          shakeTrigger={formValidated && errors.artistName ? submitCount : 0}
          onChange={(e) =>
            setFormData({ ...formData, artistName: e.target.value })
          }
        />

        <EmailInputWithSuggestions
          icon={<img src={Mail} alt="ícone email" />}
          label="E-mail"
          name="email"
          placeholder="Escreva seu e-mail"
          required
          value={formData.email}
          onChange={(val: string) => setFormData({ ...formData, email: val })}
          hasError={formValidated && errors.email}
          error={
            formValidated && errors.email ? "E-mail inválido ou vazio" : ""
          }
          shakeTrigger={formValidated && errors.email ? submitCount : 0}
        />

        <InputField
          icon={<img src={Call} alt="ícone telefone" />}
          label="Telefone"
          name="phone"
          placeholder="Escreva seu telefone"
          autoComplete="off"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
        />

        <InputField
          icon={<img src={Lock} alt="ícone senha" />}
          label="Senha"
          name="password"
          placeholder="Escreva sua senha"
          type="password"
          required
          autoComplete="off"
          value={formData.password}
          error={
            formValidated && errors.password
              ? "A senha deve ter no mínimo 6 caracteres, 1 letra maiúscula e 1 caractere especial"
              : ""
          }
          shakeTrigger={formValidated && errors.password ? submitCount : 0}
          onChange={(e) =>
            setFormData({ ...formData, password: e.target.value })
          }
        />

        <InputField
          icon={<img src={Lock} alt="ícone senha" />}
          label="Confirmar Senha"
          name="confirmPassword"
          placeholder="Confirme sua senha"
          type="password"
          required
          autoComplete="off"
          value={formData.confirmPassword}
          error={
            formValidated && errors.confirmPassword
              ? "Confirme a senha"
              : formValidated && errors.passwordMismatch
              ? "As senhas não coincidem"
              : ""
          }
          shakeTrigger={
            formValidated && (errors.confirmPassword || errors.passwordMismatch)
              ? submitCount
              : 0
          }
          onChange={(e) =>
            setFormData({ ...formData, confirmPassword: e.target.value })
          }
        />
      </InputGroup>

      <ButtonGradient type="submit">Cadastrar</ButtonGradient>

      <AnimatePresence>
        {showSuccess && (
          <SuccessToast message="Cadastro realizado com sucesso!" />
        )}
      </AnimatePresence>
    </Form>
  );
};

export default RegisterForm;
